import type { CoverageCreditIncentive } from "@redotech/redo-return-coverage-common/coverage-types";
import type { AttachmentStrategy } from "@redotech/redo-storefront-common/attachment-strategy";
import type { CartRefreshAction } from "@redotech/redo-storefront-common/cart-refresh-action";
import type { CheckboxIconPosition } from "@redotech/redo-storefront-common/checkbox-icon-position";
import type { CheckoutMethod } from "@redotech/redo-storefront-common/checkout-method";
import type {
  CheckoutUICustomizationSettings,
  Icons,
} from "@redotech/redo-storefront-common/checkout-ui-customization";
import type { FraudZipCoverageExclusionStrategy } from "@redotech/redo-storefront-common/fraud-zip-coverage-exclusion-strategy";
import type { HideRedoConfig } from "@redotech/redo-storefront-common/hide-redo-config";
import type { ToggleFields } from "@redotech/redo-storefront-common/toggle-fields";
import type {
  CartValueCalculationMethod,
  ClickStandardCheckoutButton,
  TermsAndConditions,
} from "@redotech/redo-storefront-common/widget-config";
import type { WidgetConfig } from "@redotech/redo-storefront-common/widget-schema";
import type { CheckoutButtonsSettings } from "./checkout-buttons-settings";

export const coverageVariantNamingOptions = [
  "alphanumeric",
  "custom",
  "default",
] as const;
export type CoverageVariantNaming =
  (typeof coverageVariantNamingOptions)[number];
export const CoverageVariantNaming = coverageVariantNamingOptions;

export interface ShopifyExtensionSettings {
  storefrontLogging?: {
    backendAllowedPercentage?: number;
    storefrontAllowedPercentage?: number;
    visitorObservationEnabled?: boolean;
  };
  customJs?: {
    enabled?: boolean | null;
    script?: string | null;
    readme?: string | null;
  } | null;
  exchangeBannerPositionBottom?: boolean;
  exchangeBannerInsertBeforeBody?: boolean;
  hideRedoProduct?: HideRedoConfig;
  customizationVisible?: boolean;
  customizeCartItem?: boolean;
  brandPrefix?: string;
  logo?: string;
  redoProductImage?: string;
  imageUpdated?: boolean;
  displayIcon?: boolean;
  iconColor?: string;
  iconBackgroundColor?: string;
  icon?: Icons;
  checkboxIconPosition?: CheckboxIconPosition;
  returnNameOverride?: string;
  packageProtectionNameOverride?: string;
  bothProductNameOverride?: string;
  checkoutExtensionEnabled?: boolean;
  checkoutAnalyticsInCheckoutToggleWidgetEnabled?: boolean;
  attachmentStrategy?: AttachmentStrategy;
  toggleSubtextEnabled?: boolean;
  toggleTextOptions?: {
    returnToggle: ToggleFields;
    packageProtectionToggle: ToggleFields;
    bothProductToggle: ToggleFields;
  };
  infoIconLink?: string;
  customPdpCss?: string;
  customToggleCss?: string;
  forceIPAddressLocation?: boolean;
  coverageExcludePickup?: boolean;
  usingCartAndCheckoutToggle?: boolean;
  inspiredDonationEnabled?: boolean;
  forceDoubleCheckoutToggle?: boolean;
  setCheckoutToggleBasedOnOptInCartAttribute?: boolean;
  hideCheckoutUiWhenOptedIn?: boolean;
  openShadowRoot?: boolean;
  checkoutUICustomizations?: CheckoutUICustomizationSettings;
  groupNearbyToggleElementsTogether?: boolean;
  termsAndConditions?: TermsAndConditions;
  clickStandardCheckoutButton?: ClickStandardCheckoutButton;
  widgetConfigs?: WidgetConfig[];
  checkoutButtonsSettings?: CheckoutButtonsSettings;
  redoVariantBaseName?: string;
  variantNaming?: CoverageVariantNaming;
  useBuyNowCoverage?: boolean;
  customBuyNowCoverageCss?: string;
  buyNow?: { customCss?: string };
  autoNavigateToCheckoutAfterTimeout?: boolean;
  checkoutMethod?: CheckoutMethod;
  rescueCheckoutButtonsEnabled?: boolean;
  shouldNotUseStorefrontApi?: boolean;
  disableBusinessAccountInStorefront?: boolean;
  preventPageRefreshAfterCartAdd?: boolean;
  dispatchResizeAfterItemsPlaced?: boolean;
  /** When any actions are set, they replace the built-in theme refresh hooks. */
  cartRefreshActions?: CartRefreshAction[];
  trackRedoProductInventory?: boolean;
  coverageCreditIncentive?: CoverageCreditIncentive;
  allowNonRedoVendorOnProduct?: boolean;
  lockProduct?: boolean;
  cartValueCalculationMethod?: CartValueCalculationMethod;
  // TODO: migrate customer tag based text overrides to Coverage Policies once built. See redo/merchant/app/src/setting/coverage/coverage-builder/left-panel/widget-pages/advanced-tab.tsx. $300k deal I'm sorry we needed this built super fast I tried to make it easily migrateable -Josh
  customerTagTextOverride?: {
    enabled: boolean;
    customerTags: string[];
    coverageTitle?: string;
    coverageSubtext?: string;
  };
  deleteCartProperties?: boolean;
  fraudZip?: {
    fraudZipsLastSyncedAt?: Date;
    fraudZipCoverageExclusionStrategy?: FraudZipCoverageExclusionStrategy;
  };
}

export function getCoverageVariantNaming(
  settings:
    | {
        variantNaming?: CoverageVariantNaming | null;
        redoVariantBaseName?: string | null;
      }
    | null
    | undefined,
): CoverageVariantNaming {
  return (
    settings?.variantNaming ??
    (settings?.redoVariantBaseName ? "custom" : "default")
  );
}

export function getCoverageVariantNamingToSave(
  settings: Parameters<typeof getCoverageVariantNaming>[0],
  selected: CoverageVariantNaming,
): CoverageVariantNaming | undefined {
  return !settings?.variantNaming &&
    getCoverageVariantNaming(settings) === selected
    ? undefined
    : selected;
}
